import * as React from 'react';

import once from 'lodash/once';

import SimpleLoader from '@common/react/components/UI/Loaders/SimpleLoader';

export type OtherKeys = 'pageLoader' | 'chunkLoader' | 'tableLoader' | 'autocompleteLoader';

export interface LoaderProviderProps {
	/**
	 * default loading indicator
	 */
	loader: JSX.Element;
	/**
	 * loading indicators for specific places
	 */
	other?: {
		[key in OtherKeys]?: JSX.Element;
	}
}

export interface LoaderProviderContextState {
	loaders: {
		[key in OtherKeys | 'pageLoader']?: JSX.Element;
	};
}

export interface LoaderProviderContext {
	state: LoaderProviderContextState;
	actions: { setPageLoader };
}

export const createLoaderProviderContext = once(() => React.createContext({} as LoaderProviderContext));

export const useLoaderProviderContext: () => LoaderProviderContext = () => React.useContext(createLoaderProviderContext());

/**
 * LoaderProvider component.
 *
 * usage examples:
 *  - <LoaderProvider loader={someLoader}>{React.ReactNode}</ItemProvider>
 *
 * @param props - LoaderProviderProps
 * @type {React.FC<LoaderProviderProps>}
 * @returns React.ReactElement
 */
export const LoaderProvider: React.FC<LoaderProviderProps> = ({ children, loader, other = {} }) => {
	const ItemContext = createLoaderProviderContext();

	const [pageLoader, setPageLoader] = React.useState<JSX.Element>(loader);

	const value = {
		state: {
			loaders: {
				pageLoader,
				...other,
				autocompleteLoader: other?.autocompleteLoader || <SimpleLoader />,
			},
		},
		actions: {
			setPageLoader,
		},
	};

	return (
		<ItemContext.Provider value={value}>
			{children}
		</ItemContext.Provider>
	);
};
